import { Box, Button, Tooltip } from "@mui/material";
import { SubmissionStepStatus, SubmissionStepType } from "generated-graphql/graphql";
import { useStepperNavigation } from "hooks/useStepperNavigation";
import { useTouchSubmissionMutation } from "hooks/useTouchSubmissionMutation";
import { useWasteStepper } from "hooks/useWasteStepper";
import { useCallback } from "react";

const ACKNOWLEDGE_TOOLTIP =
  "This step is read-only — acknowledging confirms you've reviewed the facility's waste activity.";

/**
 * Shared footer for stepper step editors. Records the given step complete and
 * navigates to the next step in order.
 *
 * `acknowledge` reframes the action for strictly read-only steps (e.g. Waste
 * Activity): "marking complete" makes little sense when there is nothing to
 * edit, so the button becomes "Acknowledge & continue" with a hover tooltip
 * explaining what acknowledging means. The underlying step status is still
 * Completed so the overview status icon stays meaningful.
 *
 * Renders nothing when the user cannot edit (read-only customers) or when the
 * submission is frozen (read-only after the snapshot is taken): there is no
 * continue action in either case.
 */
export function StepContinueFooter({
  submissionId,
  step,
  acknowledge = false,
}: {
  submissionId: string;
  step: SubmissionStepType;
  acknowledge?: boolean;
}) {
  const { isFrozen, canEdit } = useWasteStepper();
  const { touchStep, loading } = useTouchSubmissionMutation(submissionId);
  const { goToNextStep } = useStepperNavigation();

  const handleContinue = useCallback(async () => {
    await touchStep(step, SubmissionStepStatus.Completed);
    goToNextStep(step);
  }, [touchStep, step, goToNextStep]);

  if (isFrozen || !canEdit) {
    return null;
  }

  const button = (
    <Button variant="contained" loading={loading} onClick={handleContinue}>
      {acknowledge ? "Acknowledge & continue" : "Mark complete & continue"}
    </Button>
  );

  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
      {acknowledge ? (
        <Tooltip title={ACKNOWLEDGE_TOOLTIP}>
          <span>{button}</span>
        </Tooltip>
      ) : (
        button
      )}
    </Box>
  );
}
